import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ModalWrapper, Button, Prompt } from "@/shared/ui";
import { FileUploader } from "@/shared/components";
import { useUploadMedicalCertificateMutation } from "../api";

interface Props {
  leaveId: number;
  onClose: () => void;
  onSuccess: () => void;
}

export default function UploadCertificateModal({ leaveId, onClose, onSuccess }: Props) {
  const { t } = useTranslation("LeaveApplicationsPage");
  const [file, setFile] = useState<File | null>(null);
  const [prompt, setPrompt] = useState<{ title: string; text: string; variant: "success" | "error" } | null>(null);

  const [uploadCertificate, { isLoading: isUploading }] = useUploadMedicalCertificateMutation();

  async function handleUpload() {
    if (!file || isUploading) return;

    try {
      await uploadCertificate({ id: leaveId, file }).unwrap();
      onSuccess();
    } catch {
      setPrompt({
        title: t("messages.errorTitle"),
        text: t("messages.uploadCertificateError"),
        variant: "error",
      });
    }
  }

  function handlePromptClose() {
    setPrompt(null);
  }

  return (
    <>
      {prompt && (
        <Prompt title={prompt.title} text={prompt.text} variant={prompt.variant} onClose={handlePromptClose} />
      )}

      <ModalWrapper onClose={onClose}>
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2 border-b surface-base-stroke pb-3">
            <h4 className="text-display-2xs content-base-primary">
              {t("actions.uploadCertificate")}
            </h4>
          </div>

          <div>
            <FileUploader
              accept=".pdf"
              maxSizeMB={10}
              value={file}
              onChange={setFile}
              namespace="LeaveApplicationsPage"
              label={t("actions.selectPdf")}
            />
          </div>

          <div className="flex flex-col gap-2">
            <div className="grid grid-cols-2 gap-3">
              <Button variant="secondary" size="md" onClick={onClose} disabled={isUploading}>
                {t("buttons.cancel")}
              </Button>
              <Button variant="primary" size="md" onClick={handleUpload} disabled={!file || isUploading} textDuringDisabled={!file ? t("rules.selectFile") : undefined} fullWidthTooltip>
                {isUploading ? t("actions.uploading") : t("actions.upload")}
              </Button>
            </div>
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}

